import React from 'react';
import { useIntl } from 'react-intl';

import { SMSProviders } from '../../../api';
import { SMS_LINKS } from '../../../constants';
import { FieldInput } from '../../ui';
import {
  DescriptionLink,
  FieldWrapper,
  ProviderSubtitle,
  ProviderTitle,
  SmsProviderFieldsCard,
  SmsProviderHeader
} from '../style';
import { SMSProviderStatus } from './SMSProviderStatus';
import { ProviderFieldProps } from './types';
import { maxFieldLength, maxPhoneNumberLength, maxTwilioTokenLength } from './validator';

export const TwilioFields = ({ errors, providerData }: ProviderFieldProps): JSX.Element => {
  const intl = useIntl();
  const { TWILIO_TOKEN, TWILIO_SID, TWILIO_API, TWILIO_SENDER } = SMS_LINKS;

  const getError = (field: string): string => {
    if (errors[SMSProviders.twilio][field]) {
      const mapError: { [key: string]: number } = {
        phone_number: maxPhoneNumberLength,
        app_secret: maxTwilioTokenLength
      };

      return intl.formatMessage(
        { id: errors[SMSProviders.twilio][field] as string },
        {
          number: Object.keys(mapError).includes(field) ? mapError[field] : maxFieldLength
        }
      );
    }

    return '';
  };

  return (
    <SmsProviderFieldsCard data-test-id="sms-provider-fields-card-twilio">
      <SmsProviderHeader>
        <ProviderTitle>
          {intl.formatMessage({ id: 'custom-providers.settings.sms.provider.twilio.label' })}
        </ProviderTitle>
        <SMSProviderStatus
          providerData={providerData}
          dataTestId="sms-provider-twilio-status-button"
        />
      </SmsProviderHeader>
      <ProviderSubtitle>
        {intl.formatMessage(
          { id: 'custom-providers.settings.sms.provider.twilio.description' },
          {
            a: (chunk: string) => (
              <DescriptionLink href={TWILIO_API} target="_blank">
                {chunk}
              </DescriptionLink>
            )
          }
        )}
      </ProviderSubtitle>
      <FieldWrapper>
        <FieldInput
          label={intl.formatMessage({
            id: 'custom-providers.settings.sms.provider.twilio.sid.label'
          })}
          subLabel={intl.formatMessage(
            {
              id: 'custom-providers.settings.sms.provider.twilio.sid.sublabel'
            },
            {
              a: (chunk: string) => (
                <DescriptionLink href={TWILIO_SID} target="_blanl">
                  {chunk}
                </DescriptionLink>
              )
            }
          )}
          errorMessage={getError('app_id')}
          name={`fields.${SMSProviders.twilio}.app_id`}
        />
      </FieldWrapper>
      <FieldWrapper>
        <FieldInput
          label={intl.formatMessage({
            id: 'custom-providers.settings.sms.provider.twilio.auth-token.label'
          })}
          subLabel={intl.formatMessage(
            {
              id: 'custom-providers.settings.sms.provider.twilio.auth-token.sublabel'
            },
            {
              a: (chunk: string) => (
                <DescriptionLink href={TWILIO_TOKEN} target="_blank">
                  {chunk}
                </DescriptionLink>
              )
            }
          )}
          errorMessage={getError('app_secret')}
          name={`fields.${SMSProviders.twilio}.app_secret`}
        />
      </FieldWrapper>
      <FieldWrapper>
        <FieldInput
          label={intl.formatMessage({
            id: 'custom-providers.settings.sms.provider.twilio.phone.label'
          })}
          errorMessage={getError('phone_number')}
          name={`fields.${SMSProviders.twilio}.phone_number`}
        />
      </FieldWrapper>
      <FieldWrapper>
        <FieldInput
          label={intl.formatMessage({
            id: 'custom-providers.settings.sms.provider.sender-id.label'
          })}
          subLabel={intl.formatMessage(
            {
              id: 'custom-providers.settings.sms.provider.sender-id.sublabel'
            },
            {
              a: (chunk: string) => (
                <DescriptionLink href={TWILIO_SENDER} target="_blanl">
                  {chunk}
                </DescriptionLink>
              )
            }
          )}
          errorMessage={getError('sender_id')}
          name={`fields.${SMSProviders.twilio}.sender_id`}
        />
      </FieldWrapper>
    </SmsProviderFieldsCard>
  );
};
